import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Glyph } from "@/components/Glyph";
import { ProductImage } from "@/components/ProductImage";
import { useSeo } from "@/lib/seo";
import { useLang, lp, loc } from "@/lib/i18n";

const GALLERY = ["duo-editorial", "flacon-macro", "etui-face", "plan-serre"];

const T = {
  fr: {
    notFound: "Référence introuvable.", backToCollection: "Retour à la collection",
    back: "← Collection",
    notify: "Être averti du retour", emailPlaceholder: "Ton adresse e-mail", notifyBtn: "M'avertir",
    format: "Eau de parfum · 50 ml · Série limitée numérotée",
    pyramid: "Pyramide", top: "Tête", heart: "Cœur", base: "Fond",
    unpublished: "Composition non publiée. Elle le sera quand la formule sera arrêtée.",
    perfumer: "Parfumeur : non communiqué.",
    rrp: "Prix de vente conseillé", enquire: "Nous écrire", soldOut: "Épuisé",
    seoDesc: (p, m, pl) => `CRISOPEA ${p.roman}, ${m} sous ${pl}. Eau de parfum concentrée, 50 ml, série limitée numérotée.`,
    altDuo: (REF, m, pl) => `Étui et flacon ${REF}, ${m}, glyphe de ${pl}`,
    altGallery: { "flacon-macro": "macro du flacon", "etui-face": "étui de face", "plan-serre": "plan large", "duo-editorial": "vue éditoriale" },
  },
  en: {
    notFound: "Reference not found.", backToCollection: "Back to the collection",
    back: "← Collection",
    notify: "Be notified of its return", emailPlaceholder: "Your e-mail address", notifyBtn: "Notify me",
    format: "Eau de parfum · 50 ml · Numbered limited series",
    pyramid: "Pyramid", top: "Top", heart: "Heart", base: "Base",
    unpublished: "Composition unpublished. It will be released once the formula is settled.",
    perfumer: "Perfumer: undisclosed.",
    rrp: "Recommended retail price", enquire: "Write to us", soldOut: "Sold out",
    seoDesc: (p, m, pl) => `CRISOPEA ${p.roman}, ${m} under ${pl}. Concentrated eau de parfum, 50 ml, numbered limited series.`,
    altDuo: (REF, m, pl) => `${REF} case and bottle, ${m}, glyph of ${pl}`,
    altGallery: { "flacon-macro": "bottle macro", "etui-face": "case front", "plan-serre": "wide shot", "duo-editorial": "editorial view" },
  },
};

const NotifyForm = ({ productRef, t }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/notify", { email, ref: productRef });
      toast.success(data.message);
      setEmail("");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-8 max-w-md" data-testid="notify-form">
      <p className="text-[13px] tracking-[0.24em] uppercase text-zinc-400">{t.notify}</p>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder={t.emailPlaceholder} className="val-input mt-4" data-testid="notify-email" />
      <button type="submit" disabled={busy} className="val-btn mt-6" data-testid="notify-submit">
        {busy ? "…" : t.notifyBtn}
      </button>
    </form>
  );
};

export default function Product() {
  const { slug } = useParams();
  const lang = useLang();
  const t = T[lang];
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setP(null); setErr(false);
    api.get(`/products/crisopea-${slug}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [slug]);

  const metal = p && loc(p, lang, "metal");
  const planet = p && loc(p, lang, "planet");
  const description = p && (loc(p, lang, "description") || loc(p, lang, "ambiance"));
  const pyramid = p && loc(p, lang, "pyramid");

  useSeo(
    p ? `CRISOPEA ${p.roman} — ${metal} · Maison Partita` : "Crisopea · Maison Partita",
    p ? t.seoDesc(p, metal, planet) : ""
  );

  useEffect(() => {
    if (!p) return () => {};
    const ld = {
      "@context": "https://schema.org", "@type": "Product",
      name: `CRISOPEA ${p.roman} — ${p.metal}`, description: p.description || p.ambiance,
      brand: { "@type": "Brand", name: "Crisopea" },
      offers: { "@type": "Offer", priceCurrency: "EUR", price: p.price,
        availability: p.available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(ld);
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, [p]);

  if (err) return <div className="max-w-3xl mx-auto px-5 py-40 text-center text-zinc-400">{t.notFound} <Link to={lp(lang, "/crisopea")} className="val-link underline">{t.backToCollection}</Link></div>;
  if (!p) return <div className="py-40 text-center text-zinc-600 tracking-[0.2em] uppercase text-[13px]">…</div>;

  const REF = `CRISOPEA ${p.roman}`;

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-16 pb-24">
      <Link to={lp(lang, "/crisopea")} className="val-link text-[13px] tracking-[0.2em] uppercase" data-testid="back-link">{t.back}</Link>

      <div className="mt-12 grid gap-16 lg:grid-cols-2">
        <div className="space-y-8">
          <Reveal>
            <div className="overflow-hidden bg-zinc-900 border border-zinc-800">
              <ProductImage maison="crisopea" reference={p.reference} suffix="duo" eager
                alt={t.altDuo(REF, metal, planet)} />
            </div>
          </Reveal>
          {p.available && (
            <div className="grid grid-cols-2 gap-8">
              {GALLERY.map((s, i) => (
                <Reveal key={s} delay={0.1 + i * 0.08}>
                  <div className={`overflow-hidden bg-zinc-900 border border-zinc-800 ${s === "plan-serre" ? "col-span-2" : ""}`}>
                    <ProductImage maison="crisopea" reference={p.reference} suffix={s}
                      alt={`${REF}, ${metal} — ${t.altGallery[s]}`} />
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>

        <div className="lg:pt-6">
          <Reveal>
            <div className="flex items-center gap-6">
              <Glyph glyph={p.glyph} color={p.color} size={96} />
              <div>
                <h1 className="text-3xl sm:text-4xl tracking-[0.16em] uppercase text-zinc-50">{REF}</h1>
                <p className="mt-2 text-zinc-400 tracking-[0.1em]">{metal} · {planet}</p>
              </div>
            </div>

            <p className="mt-8 text-[13px] tracking-[0.2em] uppercase text-zinc-500">{t.format}</p>

            <p className="mt-10 text-[19px] leading-[1.85] text-zinc-300">{description}</p>

            {pyramid ? (
              <div className="mt-12 space-y-4">
                <p className="text-[13px] tracking-[0.24em] uppercase text-zinc-500">{t.pyramid}</p>
                <Row label={t.top} v={pyramid.tete} />
                <Row label={t.heart} v={pyramid.coeur} />
                <Row label={t.base} v={pyramid.fond} />
              </div>
            ) : (
              !p.available && <p className="mt-8 text-zinc-500 italic">{t.unpublished}</p>
            )}

            <p className="mt-10 text-zinc-500">{t.perfumer}</p>

            <div className="mt-12 border-t border-zinc-900 pt-10">
              {p.available ? (
                <div data-testid="product-available">
                  <p className="text-3xl text-zinc-100">{p.price} €</p>
                  <p className="mt-2 text-[13px] tracking-[0.16em] uppercase text-zinc-600">{t.rrp}</p>
                  <Link to={lp(lang, "/contact")} className="val-btn mt-8" data-testid="product-enquire">{t.enquire}</Link>
                </div>
              ) : (
                <div data-testid="product-sold-out">
                  <p className="text-[13px] tracking-[0.2em] uppercase text-zinc-500">{t.soldOut}</p>
                  <NotifyForm productRef={REF} t={t} />
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, v }) => (
  <div className="flex gap-6">
    <span className="w-16 shrink-0 text-[13px] tracking-[0.14em] uppercase text-zinc-600">{label}</span>
    <span className="text-zinc-300">{v}</span>
  </div>
);
